import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useLocation, useSearch } from "wouter";
import { Layout } from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, FileText, Copy } from "lucide-react";
import { StepIndicator } from "@/components/onboarding/StepIndicator";
import { useOrderTemplates } from "@/hooks/use-order-templates";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STEP_TITLES = ["Welcome", "Invite Team Members", "Start an Order"];

export default function ContractorOnboardingLanding() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const urlParams = new URLSearchParams(search);
  const memberCount = parseInt(urlParams.get("count") || "0", 10);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  // Fetch order templates
  const { data: templates = [], isPending: templatesLoading } =
    useOrderTemplates();
  const hasTemplates = templates && templates.length > 0;

  const handleCreateBlankOrder = () => {
    setLocation("/orders/new");
  };

  const handleUseTemplate = () => {
    if (selectedTemplateId) {
      setLocation(`/orders/new?template=${selectedTemplateId}`);
    }
  };

  const handleViewAllTemplates = () => {
    setLocation("/admin/order-templates");
  };

  const handleBack = () => {
    setLocation("/c-onboarding/invite");
  };

  const handleBackToWelcome = () => {
    setLocation("/c-onboarding/welcome");
  };

  const handleStepClick = (stepNumber: number) => {
    if (stepNumber === 1) {
      setLocation("/c-onboarding/welcome");
    } else if (stepNumber === 2) {
      setLocation("/c-onboarding/invite");
    }
  };

  return (
    <Layout hideSidebar>
      <div className="max-w-4xl mx-auto">
        <StepIndicator
          currentStep={3}
          totalSteps={3}
          stepTitles={STEP_TITLES}
          onStepClick={handleStepClick}
        />
        <div className="text-center mb-12 space-y-4">
          {memberCount > 0 && (
            <>
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-primary/10 rounded-full">
                  <CheckCircle2 className="h-12 w-12 text-primary" />
                </div>
              </div>
              <h1 className="text-4xl font-bold">{t("pages.cOnboarding.landing.youReAllSet")}</h1>
            </>
          )}
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {memberCount > 0
              ? t("pages.cOnboarding.landing.weVeReceivedYour")
              : t("pages.cOnboarding.landing.welcomeToOnsemblePro")}
          </p>
        </div>

        <Card className="border-2 mb-8 bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="text-center space-y-3">
              <h2 className="text-2xl font-semibold">
                {t("pages.cOnboarding.landing.readyToStartOrdering")}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t("pages.cOnboarding.landing.youCanStartCreating")}
              </p>
              <p className="text-sm font-medium text-foreground">
                {t("pages.cOnboarding.landing.getStartedByCreating")}
              </p>
            </div>
          </CardContent>
        </Card>

        <div
          className={`grid gap-6 mb-8 ${
            hasTemplates ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
          }`}
        >
          {/* Create Blank Order Card */}
          <Card className="border-2 hover:border-primary/50 transition-colors">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <FileText className="h-6 w-6 text-primary" />
                  </div>
                  <h2 className="text-xl font-semibold">{t("pages.cOnboarding.landing.createBlankOrder")}</h2>
                </div>

                <p className="text-muted-foreground">
                  {t("pages.cOnboarding.landing.startANewOrder")}
                </p>

                <Button
                  onClick={handleCreateBlankOrder}
                  className="w-full"
                  size="lg"
                >
                  {t("pages.cOnboarding.landing.createOrder")}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Use Template Card - Only show if templates exist */}
          {hasTemplates && (
            <Card className="border-2 hover:border-primary/50 transition-colors">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Copy className="h-6 w-6 text-primary" />
                    </div>
                    <h2 className="text-xl font-semibold">
                      {t("pages.cOnboarding.landing.useExistingTemplate")}
                    </h2>
                  </div>

                  <p className="text-muted-foreground">
                    {t("pages.cOnboarding.landing.startFromASaved")}
                  </p>

                  {templatesLoading ? (
                    <div className="text-sm text-muted-foreground">
                      {t("pages.cOnboarding.landing.loadingTemplates")}
                    </div>
                  ) : (
                    <>
                      <Select
                        value={selectedTemplateId}
                        onValueChange={setSelectedTemplateId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t("pages.cOnboarding.landing.selectATemplate")} />
                        </SelectTrigger>
                        <SelectContent>
                          {templates
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((template) => (
                              <SelectItem
                                key={template.uid}
                                value={template.uid}
                              >
                                {template.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>

                      <Button
                        onClick={handleUseTemplate}
                        className="w-full"
                        size="lg"
                        disabled={!selectedTemplateId}
                      >
                        {t("pages.cOnboarding.landing.useTemplate")}
                      </Button>

                      <Button
                        onClick={handleViewAllTemplates}
                        className="w-full"
                        size="lg"
                      >
                        {t("pages.cOnboarding.landing.viewAllTemplates")}
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Navigation Card */}
        <Card className="bg-primary/5 border-2 border-primary/20 mt-8">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                variant="outline"
                onClick={handleBackToWelcome}
                className="min-w-48 h-12 text-lg flex items-center gap-2"
              >
                {t("pages.cOnboarding.landing.backToWelcome")}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleBack}
                className="min-w-48 h-12 text-lg flex items-center gap-2"
              >
                {t("pages.cOnboarding.landing.backToInviteTeam")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
